import React, { useCallback, useRef, useState, useEffect } from 'react'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import GPCard from './GpCard'
import { getLatLngForPostcode, getAddressFromLatLng } from '../api/getAddress'

const containerStyle = {
  width: '100%',
  height: '320px',
}

const defaultCenter = {
  lat: 51.5138485,
  lng: -0.1427234,
}

const GPMapSelection = ({ onChoose, onHideMap, selectedGP, setSelectedGP }) => {
  const [map, setMap] = useState(null)
  const [markers, setMarkers] = useState([])
  const searchInputRef = useRef(null)
  const [isGPValid, setIsGpValid] = useState(false)
  const infoWindowRef = useRef(null)
  const [useMyLocation, setUseMyLocation] = useState(false)
  const nearbySearchRequestIdRef = useRef(0)
  const gpCardRefs = useRef([])
  gpCardRefs.current = markers.map(
    (_, i) => gpCardRefs.current[i] ?? React.createRef()
  )

  useEffect(() => {
    if (!map) return

    const timer = setTimeout(() => {
      if (!navigator.geolocation) {
        setUseMyLocation(false)
        searchNearbyGPs(
          new window.google.maps.LatLng(defaultCenter.lat, defaultCenter.lng)
        )
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          setUseMyLocation(true)
          map.panTo(loc)
          map.setZoom(14)
          searchNearbyGPs(loc)
        },
        () => {
          setUseMyLocation(false)
          searchNearbyGPs(
            new window.google.maps.LatLng(defaultCenter.lat, defaultCenter.lng)
          )
        },
        { timeout: 8000 }
      )
    }, 250)

    return () => clearTimeout(timer)
  }, [map])

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  })

  const getAddressComponent = (components, type) => {
    const comp = components.find((c) => c.types.includes(type))
    return comp ? comp.long_name : ''
  }

  const handleMarkerClick = (idx) => {
    if (gpCardRefs.current[idx]?.current) {
      gpCardRefs.current[idx].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance)
    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow()
    }
  }, [])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  const isLikelyUkPostcode = (value) => {
    const v = (value || '').trim().toUpperCase()
    if (!v) return false
    if (v === 'GIR 0AA') return true
    return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(v)
  }

  const handleSearch = async () => {
    const address = searchInputRef.current.value
    if (!address || !map) return

    setUseMyLocation(false)

    // Option B: UK postcode -> Ideal Postcodes; otherwise fallback to Google geocoding.
    if (isLikelyUkPostcode(address)) {
      try {
        const { lat, lng, post_town, postcode } = await getLatLngForPostcode(address)
        const location = { lat, lng, post_town, postcode }
        map.panTo(location)
        map.setZoom(14)
        searchNearbyGPs(location)
        return
      } catch (e) {
        // If Ideal fails for any reason, fall back to Google geocoding.
        console.warn('Ideal Postcodes lookup failed; falling back to Google.', e)
      }
    }

    const geocoder = new window.google.maps.Geocoder()
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location
        map.panTo(location)
        map.setZoom(14)
        searchNearbyGPs(location)
      } else {
        alert('Could not find that location. Try a UK postcode instead.')
      }
    })
  }

  const handleUseMyLocationToggle = () => {
    if (useMyLocation) {
      setUseMyLocation(false)
    } else {
      setUseMyLocation(true)
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            

            try {
              const address = await getAddressFromLatLng(
                position.coords.latitude,
                position.coords.longitude
              )
              const loc = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                post_town: address.town || address.city || '',
              }

              map.panTo(loc)
              map.setZoom(14)
              searchNearbyGPs(loc)
            } catch (error) {
              console.error(error)
            }
          },
          () =>
            alert(
              'Error fetching your location. Please try again or enter location manually.'
            )
        )
      } else {
        alert('Geolocation is not supported by your browser.')
      }
    }
  }

  const searchNearbyGPs = (location, mapRef = map) => {
    if (!mapRef) return
    console.log('location: ', location)
    const requestId = ++nearbySearchRequestIdRef.current

    const service = new window.google.maps.places.PlacesService(mapRef)
    const request = {
      location,
      radius: 3000,
      keyword: 'doctor',
    }

    service.nearbySearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        // Ignore stale responses (e.g., user searches again quickly)
        if (requestId !== nearbySearchRequestIdRef.current) return
        const filtered = (results || []).filter(Boolean).slice(0, 20)
        const nextMarkers = filtered.map((place) => {
          const placeId = place.place_id
          const position = place.geometry?.location
          return {
            placeId,
            gp: {
              name: place.name,
              address: place.vicinity,
              city: location.post_town,
              postcode: location.postcode,
              rating: place.rating,
            },
            position: position
              ? { lat: position.lat(), lng: position.lng() }
              : undefined,
            name: place.name,
            address: place.vicinity,
          }
        })

        setMarkers(nextMarkers.filter((m) => m.position))
      }
    })
  }

  return isLoaded ? (
    <div className="space-y-6">
      <div>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={defaultCenter}
          zoom={14}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            maxZoom: 18,
            minZoom: 14,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {markers.map((marker, idx) => (
            <Marker
              key={idx}
              position={marker.position}
              title={marker.name}
              onClick={() => {
                if (infoWindowRef.current) {
                  const content = `
                    <div>
                      <h3 style="margin:0;">${marker.name}</h3>
                      ${marker.address
                      ? `<p style="margin:0;">${marker.address}</p>`
                      : ''
                    }
                    </div>
                  `
                  infoWindowRef.current.setContent(content)
                  infoWindowRef.current.setPosition(marker.position)
                  infoWindowRef.current.open(map)
                }
                handleMarkerClick(idx)
              }}
            />
          ))}
        </GoogleMap>
      </div>

      <div>
        <label className="block font-medium text-base mb-3">
          Search by your current location, or enter a postcode/ location to
          narrow the results.
        </label>

        <div className="flex flex-col sm:flex-row w-full gap-4">
          <label
            className={`flex items-center gap-2 border rounded-md px-4 py-2 cursor-pointer w-full sm:w-1/2 ${useMyLocation
                ? 'bg-[#F7F5FB] border-[#7B5FA6] text-[#7B5FA6]'
                : 'bg-white border-gray-300 text-black'
              }`}
          >
            <input
              type="radio"
              name="locationOption"
              checked={useMyLocation}
              onChange={handleUseMyLocationToggle}
              className="h-4 w-4 cursor-pointer accent-[#7B5FA6]"
            />
            <span className="text-sm font-medium">Use your location</span>
          </label>

          <div className="flex w-full sm:w-1/2 border rounded-md overflow-hidden">
            <input
              type="text"
              placeholder="Enter location"
              className="flex-1 px-4 py-2 text-sm outline-none"
              ref={searchInputRef}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSearch()
                }
              }}
            />
            <button
              type="button"
              className="bg-[#7B5FA6] hover:bg-[#DDCCDF] text-white font-semibold text-sm px-5"
              onClick={handleSearch}
            >
              Search
            </button>
          </div>
        </div>

        <div className="mt-6 mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className="text-sm text-gray-600 max-w-xl">
            The location service may fail to detect your location or produce
            inaccurate results. If you cannot find the desirable result below,
            please go back to the last page and enter the details manually.
          </p>
          <button
            type="button"
            className="bg-white text-black text-base font-semibold px-6 py-3 rounded-xl shadow border whitespace-nowrap"
            onClick={onHideMap}
          >
            Enter details manually
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {markers.map((marker, idx) => {
          return (
            <GPCard
              key={idx}
              gp={marker.gp}
              marker={marker}
              onChoose={onChoose}
              idx={idx}
              gpCardRefs={gpCardRefs}
            />
          )
        })}
      </div>

      {isGPValid && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-[3px] z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">{selectedGP.name}</h2>
            <p className="mb-1">{selectedGP.address}</p>
            <p className="mb-1">{selectedGP.postcode}</p>
            <p className="mb-2">
              {selectedGP.address || 'No address available'}
            </p>
            {selectedGP.phone && (
              <p className="mb-2">Phone: {selectedGP.phone}</p>
            )}
            {selectedGP.rating && (
              <p className="mb-2">⭐ Rating: {selectedGP.rating}</p>
            )}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setSelectedGP(null)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : (
    <p>Loading map...</p>
  )
}

export default GPMapSelection
